import { motion } from "framer-motion";
import { Beef, Carrot, Clock, Timer, Users, Utensils } from "lucide-react";
import type { DietaryPreference, Event, Rsvp } from "~/lib/api/types";
import { cn } from "~/lib/utils";

type HostInsightsProps = {
	rsvps: Rsvp[];
	event: Event;
	className?: string;
};

const DIETARY_PREFERENCE_LABELS: Record<
	DietaryPreference,
	{ label: string; icon: React.ReactNode }
> = {
	vegetarian: {
		label: "Vegetarian",
		icon: <Carrot className="size-4 text-accent" strokeWidth={2} />,
	},
	non_vegetarian: {
		label: "Non-Vegetarian",
		icon: <Beef className="size-4 text-accent" strokeWidth={2} />,
	},
};

export const HostInsights = ({
	rsvps,
	event,
	className,
}: HostInsightsProps) => {
	// Guest Stats
	const confirmedGuests = rsvps.filter((r) => r.status === "accepted");
	const guestsWithPlusOnes = confirmedGuests.filter((r) => r.bringingPlusOne);
	const pendingGuests = rsvps.filter((r) => r.status === "pending");
	const maybeGuests = rsvps.filter((r) => r.status === "maybe");

	// Calculate total confirmed including +1s
	const totalConfirmed = confirmedGuests.length + guestsWithPlusOnes.length;

	// Time calculations
	const now = new Date();
	const startDate = new Date(event.startDate);

	const isStartingSoon =
		startDate.getTime() - now.getTime() <= 24 * 60 * 60 * 1000; // 24 hours
	const isInProgress = startDate <= now;

	// Get guests who haven't provided their +1's name
	const guestsNeedingPlusOneName = confirmedGuests.filter(
		(r) => r.bringingPlusOne && !r.plusOneName,
	);

	const dietaryPreferences = rsvps.reduce(
		(acc, current) => {
			if (current.dietaryPreference) {
				acc[current.dietaryPreference] =
					(acc[current.dietaryPreference] || 0) + 1;
			}
			if (current.plusOneDietaryPreference) {
				acc[current.plusOneDietaryPreference] =
					(acc[current.plusOneDietaryPreference] || 0) + 1;
			}
			return acc;
		},
		{} as Record<string, number>,
	);

	const container = {
		hidden: { opacity: 0 },
		show: {
			opacity: 1,
			transition: {
				staggerChildren: 0.1,
			},
		},
	};

	const item = {
		hidden: { opacity: 0, y: 10 },
		show: { opacity: 1, y: 0 },
	};

	return (
		<motion.div
			variants={container}
			initial="hidden"
			animate="show"
			className={cn("space-y-4", className)}
		>
			{/* Event Status */}
			{(isStartingSoon || isInProgress || event.status === "cancelled") && (
				<motion.div
					variants={item}
					className="bg-gradient-to-b from-white/5 to-white/[0.02] backdrop-blur-xl rounded-3xl border border-white/10 p-6 hover:border-white/20 transition-colors"
				>
					<div className="flex items-center gap-3 mb-4">
						<div className="p-2.5 rounded-xl bg-accent/10 border border-accent/20">
							<Timer className="w-5 h-5 text-accent" strokeWidth={2} />
						</div>
						<h3 className="text-lg font-semibold text-white">Event Status</h3>
					</div>
					<div className="space-y-3">
						{event.status === "cancelled" ? (
							<div className="flex justify-between items-center">
								<span className="text-white/70">Status</span>
								<span className="text-destructive font-medium">Cancelled</span>
							</div>
						) : (
							<>
								{isStartingSoon && !isInProgress && (
									<div className="flex justify-between items-center">
										<span className="text-white/70">Starting Soon</span>
										<span className="text-accent font-medium">
											Within 24 hours
										</span>
									</div>
								)}
								{isInProgress && (
									<div className="flex justify-between items-center">
										<span className="text-white/70">Status</span>
										<span className="text-[#22c55e] font-medium">
											In Progress
										</span>
									</div>
								)}
							</>
						)}
					</div>
				</motion.div>
			)}

			{/* Guest Summary */}
			<motion.div
				variants={item}
				className="bg-primary/10 backdrop-blur-xl rounded-3xl border border-white/10 p-6 hover:border-white/20 transition-colors"
			>
				<div className="flex items-center gap-3 mb-4">
					<div className="p-2.5 rounded-xl bg-accent/10 border border-accent/20">
						<Users className="w-5 h-5 text-accent" strokeWidth={2} />
					</div>
					<h3 className="text-lg font-semibold text-white">Guest Summary</h3>
				</div>
				<div className="space-y-3">
					<div className="flex justify-between items-center">
						<span className="text-white/70">Total Confirmed</span>
						<span className="text-white font-medium">
							{totalConfirmed} {totalConfirmed === 1 ? "person" : "people"}
						</span>
					</div>
					{guestsWithPlusOnes.length > 0 && (
						<div className="flex justify-between items-center">
							<span className="text-white/70">Including +1s</span>
							<span className="text-accent/90 font-medium">
								{guestsWithPlusOnes.length} guests bringing someone
							</span>
						</div>
					)}
					{event.guestLimit && (
						<div className="flex justify-between items-center">
							<span className="text-white/70">Guest Limit</span>
							<div className="flex items-center gap-2">
								<span
									className={cn(
										"font-medium",
										totalConfirmed >= event.guestLimit && "text-destructive",
										totalConfirmed >= event.guestLimit * 0.8 && "text-accent",
									)}
								>
									{event.guestLimit - totalConfirmed} spots left
								</span>
								<span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-primary/50 text-sm text-primary-foreground">
									{event.guestLimit} max
								</span>
							</div>
						</div>
					)}
					{maybeGuests.length > 0 && (
						<div className="flex justify-between items-center">
							<span className="text-white/70">Maybe Coming</span>
							<span className="text-amber-500 font-medium">
								{maybeGuests.length} undecided
							</span>
						</div>
					)}
					{pendingGuests.length > 0 && (
						<div className="flex justify-between items-center">
							<span className="text-white/70">Awaiting Response</span>
							<span className="text-white/90 font-medium">
								{pendingGuests.length} pending
							</span>
						</div>
					)}
				</div>
			</motion.div>

			{/* Dietary Preferences */}
			{event.collectDietaryPrefs && (
				<motion.div
					variants={item}
					className="bg-primary/10 backdrop-blur-xl rounded-3xl border border-white/10 p-6 hover:border-white/20 transition-colors"
				>
					<div className="flex items-center gap-3 mb-4">
						<div className="p-2.5 rounded-xl bg-accent/10 border border-accent/20">
							<Utensils className="w-5 h-5 text-accent" strokeWidth={2} />
						</div>
						<h3 className="text-lg font-semibold text-white">
							Dietary Preferences
						</h3>
					</div>
					<div className="space-y-3">
						{Object.entries(dietaryPreferences).map(
							([dietaryPreference, count]) => (
								<div
									className="flex justify-between items-center"
									key={dietaryPreference}
								>
									<div className="flex items-center gap-2">
										{
											DIETARY_PREFERENCE_LABELS[
												dietaryPreference as DietaryPreference
											].icon
										}
										<span className="text-white/90 font-medium">
											{
												DIETARY_PREFERENCE_LABELS[
													dietaryPreference as DietaryPreference
												].label
											}
										</span>
									</div>
									<div className="text-white/90 font-medium">
										{count} {count === 1 ? "person" : "people"}
									</div>
								</div>
							),
						)}
					</div>
				</motion.div>
			)}

			{/* Action Items */}
			{guestsNeedingPlusOneName.length > 0 && (
				<motion.div
					variants={item}
					className="bg-primary/10 backdrop-blur-xl rounded-3xl border border-white/10 p-6 hover:border-white/20 transition-colors"
				>
					<div className="flex items-center gap-3 mb-4">
						<div className="p-2.5 rounded-xl bg-accent/10 border border-accent/20">
							<Clock className="w-5 h-5 text-accent" strokeWidth={2} />
						</div>
						<h3 className="text-lg font-semibold text-white">Action Items</h3>
					</div>
					<div className="space-y-3">
						{guestsNeedingPlusOneName.length > 0 && (
							<div className="flex justify-between items-center">
								<span className="text-white/70">Missing +1 Names</span>
								<span className="text-white/90 font-medium">
									{guestsNeedingPlusOneName.length} guests
								</span>
							</div>
						)}
					</div>
				</motion.div>
			)}
		</motion.div>
	);
};
